import { ReactNode, useEffect, useState } from 'react';
import { alpha, Box, lighten, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import Joyride, { CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import { useIntl } from 'react-intl';
import { useTour } from 'src/contexts/TourContext';
import { buildDynamicTourSteps } from 'src/config/tourSteps';

import Sidebar from './Sidebar';
import Header from './Header';

interface SidebarLayoutProps {
  children?: ReactNode;
  language: string;
  onSelectLanguage: (item: string) => void;
}

const SidebarLayout = (props: SidebarLayoutProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const { runTour, stopTour } = useTour();
  const location = useLocation();
  const [tourSteps, setTourSteps] = useState<Step[]>([]);
  const [tourReady, setTourReady] = useState(false);

  useEffect(() => {
    if (!runTour) {
      setTourReady(false);
      return;
    }
    // Delay to let child components render their tour target elements
    const timer = setTimeout(() => {
      const isInsideInstallation = location.pathname.includes('/installation/');
      const steps = buildDynamicTourSteps(intl, isInsideInstallation);
      setTourSteps(steps);
      setTourReady(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [runTour, location.pathname, intl]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, step, type } = data;
    if (type === EVENTS.STEP_BEFORE && step?.target) {
      const el = document.querySelector(step.target as string);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      stopTour();
    }
  };

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={tourReady}
        callback={handleJoyrideCallback}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableOverlayClose
        styles={{
          options: {
            primaryColor: '#ffc04d',
            zIndex: 10000
          }
        }}
      />
      <Box
        sx={{
          flex: 1,
          height: '100%',

          '.MuiPageTitle-wrapper': {
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.trueWhite[5]
                : theme.colors.alpha.white[50],
            marginBottom: `${theme.spacing(4)}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 1px 0 ${alpha(
                    lighten(theme.colors.primary.main, 0.7),
                    0.15
                  )}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
                : `0px 2px 4px -3px ${alpha(
                    theme.colors.alpha.black[100],
                    0.1
                  )}, 0px 5px 12px -4px ${alpha(
                    theme.colors.alpha.black[100],
                    0.05
                  )}`
          }
        }}
      >
        <div style={{ userSelect: 'none' }}>
          <Header
            language={props.language}
            onSelectLanguage={props.onSelectLanguage}
          />
        </div>
        <Sidebar />
        <Box
          sx={{
            position: 'relative',
            zIndex: 5,
            display: 'block',
            flex: 1,
            pt: `${theme.header.height}`,
            [theme.breakpoints.up('lg')]: {
              ml: '260px'
            }
          }}
        >
          <Box display="block">
            <Outlet />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SidebarLayout;
